import { assetUrl, fetchAsset } from "../core/AssetSource.js";
import { bindTabList, selectTab } from "../utils/ui.js";

const TAB_KEY = "feydom_help_tab";
const tutorialId = (tab) => tab.dataset.tutorial;

/** Help tabs: one shared <video> loads only the shown clip; <id>.json locale-key cues subtitle it in every language. */
export class HelpTutorials {
  constructor(app, uiManager) {
    this.app = app;
    this.ui = uiManager;
    this._bound = false;
    this._current = null;
    this._cueSheets = new Map();
    this._cueToken = 0;
    this._track = null;
  }

  get _modal() { return document.getElementById("shortcuts-modal"); }
  get _video() { return document.getElementById("help-video"); }
  get _caption() { return document.getElementById("help-caption"); }
  _tabs() { return [...(this._modal?.querySelectorAll(".help-tab") ?? [])]; }

  open() {
    if (!this._modal) return;
    this._bind();
    let saved = null;
    try { saved = localStorage.getItem(TAB_KEY); } catch { /* storage blocked */ }
    const tabs = this._tabs();
    const tab = tabs.find((t) => t.dataset.tutorial === saved) ?? tabs[0];
    // Focus lands on the tab so the arrow keys work straight away, as in any dialog.
    if (tab) this.activate(tab.dataset.tutorial, { focus: true });
  }

  close() {
    this._video?.pause();
  }

  activate(id, { focus = false } = {}) {
    const modal = this._modal;
    if (!modal) return;
    this._current = id;

    selectTab(this._tabs(), tutorialId, id, focus)?.scrollIntoView({ block: "nearest", inline: "nearest" });
    this._syncFade();
    let panel = null;
    for (const p of modal.querySelectorAll(".help-panel")) {
      p.hidden = p.dataset.tutorial !== id;
      if (!p.hidden) panel = p;
    }
    try { localStorage.setItem(TAB_KEY, id); } catch { /* storage blocked */ }

    const hasVideo = panel?.hasAttribute("data-video");
    modal.querySelector(".help-media")?.classList.toggle("hidden", !hasVideo);
    if (hasVideo) this._play(id);
    else this._video?.pause();
  }

  _play(id) {
    const video = this._video;
    if (!video) return;
    if (video.dataset.tutorial !== id) {
      video.dataset.tutorial = id;
      video.poster = assetUrl(`tutorials/${id}.jpg`);
      video.src = assetUrl(`tutorials/${id}.mp4`);
      this._loadCaptions(id);
    }
    if (this._reducedMotion()) {
      video.controls = true;
      return;
    }
    video.controls = false;
    video.currentTime = 0;
    // A rejected play() (autoplay policy, unsupported codec) leaves the poster up, which still explains the step.
    video.play().catch(() => { video.controls = true; });
  }

  _loadCaptions(id) {
    const track = this._captionTrack();
    if (!track) return;
    for (const cue of [...(track.cues ?? [])]) track.removeCue(cue);
    this._showCaption(null);
    let sheet = this._cueSheets.get(id);
    if (!sheet) {
      sheet = fetchAsset(`tutorials/${id}.json`)
        .then((res) => (res.ok ? res.json() : null))
        .then((json) => json?.cues ?? [])
        .catch(() => []);
      this._cueSheets.set(id, sheet);
    }
    // A tab switched before the sheet arrived must not get the previous clip's cues.
    const token = ++this._cueToken;
    sheet.then((cues) => {
      if (token !== this._cueToken) return;
      for (const { key, start, end } of cues) track.addCue(new VTTCue(start, end, key));
    });
  }

  /** A hidden metadata track: the browser keeps cue timing across loops and seeks, the overlay draws the text. */
  _captionTrack() {
    const video = this._video;
    if (this._track || !video?.addTextTrack || typeof VTTCue === "undefined") return this._track;
    this._track = video.addTextTrack("metadata");
    this._track.mode = "hidden";
    this._track.addEventListener("cuechange", () => this._showCaption(this._track.activeCues?.[0]?.text ?? null));
    return this._track;
  }

  _showCaption(key) {
    const el = this._caption;
    if (!el) return;
    el.hidden = !key;
    if (!key) {
      el.removeAttribute("data-i18n");
      return;
    }
    // data-i18n lets a language switch re-translate the line on screen.
    el.dataset.i18n = key;
    el.textContent = this.ui.t(key);
  }

  _syncFade() {
    const list = this._modal?.querySelector(".help-tabs");
    if (!list) return;
    const max = list.scrollWidth - list.clientWidth;
    list.classList.toggle("fade-start", list.scrollLeft > 1);
    list.classList.toggle("fade-end", list.scrollLeft < max - 1);
  }

  _reducedMotion() {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  }

  _bind() {
    if (this._bound) return;
    this._bound = true;
    const list = this._modal.querySelector(".help-tabs");
    list?.addEventListener("scroll", () => this._syncFade(), { passive: true });
    window.addEventListener("resize", () => this._syncFade());
    bindTabList(list, () => this._tabs(), tutorialId, () => this._current, (id, opts) => this.activate(id, opts));
    // Kept from EventManager's document listener, which would read it as "deselect".
    this._modal.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      this.ui.closeShortcutsModal();
    });
    this._video?.addEventListener("click", () => {
      const v = this._video;
      if (v.controls) return;
      if (v.paused) v.play().catch(() => {});
      else v.pause();
    });
  }
}
